import { safeFormatDecimal, safeFormatPercent, safeFormatUSD } from '@/utils';
import {
  BatteryUnknown,
  MonetizationOn,
  RequestQuote,
  Schedule,
} from '@mui/icons-material';
import { Box, Divider, Skeleton, Stack, Typography } from '@mui/material';
import SvgIcon from '@mui/material/SvgIcon';
import React from 'react';
import { SystemCardPieChart } from '../components/SystemCardPieChart';
import { EnergyKind } from '../types';

type MuiIconComponent = typeof SvgIcon;

type Props = {
  annualCost: number;
  capitalCost: number;
  coverageFieldTitle: string;
  coveragePercentage: number;
  designLife?: number | string;
  energyDemand: number;
  energySource: EnergyKind;
  EnergySourceIcon: MuiIconComponent;
  isLoading?: boolean;
  limit?: number;
  name: string;
  /**
   * Replaces the "$X/unit" capital cost value with an explanatory label.
   * Used for passive technologies, whose capital cost the backend derives from
   * the building's design cost rather than from `capitalCost` x units, so the
   * per-unit figure would be a number the user never gets charged.
   */
  capitalCostNote?: React.ReactNode;
};

export const SystemCardPopoverContent: React.FC<Props> = ({
  annualCost,
  capitalCost,
  coverageFieldTitle,
  coveragePercentage,
  designLife,
  energyDemand,
  energySource,
  EnergySourceIcon,
  isLoading,
  limit,
  name,
  capitalCostNote,
}) => {
  return (
    <Stack
      spacing={{ sm: 1, md: 2 }}
      fontFamily={'subtitle1.fontFamily'}
      fontSize={{ sm: 8, md: 10, lg: 12 }}
      fontWeight={500}
    >
      {/** TITLE */}
      <Stack direction={'row'} justifyContent={'space-between'} width={'100%'}>
        <Box flex={1}>
          <Typography
            variant={'subtitle2'}
            textTransform={'uppercase'}
            fontFamily={'inherit'}
            fontSize={{ sm: 12, md: 14, lg: 16 }}
            lineHeight={1}
          >
            {name}
          </Typography>
        </Box>
        <Stack
          direction={'row'}
          spacing={0.5}
          alignItems={'center'}
          alignSelf={'flex-start'}
        >
          {EnergySourceIcon ? (
            <EnergySourceIcon fontSize={'small'} />
          ) : (
            <BatteryUnknown fontSize={'small'} />
          )}
          <Typography fontSize={'inherit'} variant={'subtitle2'}>
            {energySource}
          </Typography>
        </Stack>
      </Stack>
      <Stack width={'100%'} spacing={1}>
        {/**first row - coveragefield Coverage*/}
        <Stack
          direction={'row'}
          alignItems={'center'}
          justifyContent={'space-between'}
          spacing={1}
        >
          <Stack direction={'row'} alignItems={'center'} spacing={1}>
            <SystemCardPieChart width={17} coverage={limit ? 1 / limit : 0} />
            <Typography
              fontSize={'inherit'}
              fontFamily={'inherit'}
              fontWeight={'inherit'}
              textTransform={'capitalize'}
            >
              {coverageFieldTitle} Coverage
            </Typography>
          </Stack>
          <Typography
            fontSize={'inherit'}
            fontFamily={'inherit'}
            fontWeight={'bold'}
          >
            {isLoading ? (
              <Skeleton variant='text' width={60} />
            ) : (
              `${safeFormatPercent(coveragePercentage, 'SystemCardPopover.coverage')}/unit`
            )}
          </Typography>
        </Stack>
        {/**second row - Energy Demand*/}
        <Stack
          direction={'row'}
          alignItems={'center'}
          justifyContent={'space-between'}
          spacing={1}
        >
          <Stack direction={'row'} alignItems={'center'} spacing={1}>
            {EnergySourceIcon ? (
              <EnergySourceIcon fontSize={'small'} color={'primary'} />
            ) : (
              <BatteryUnknown fontSize={'small'} color={'primary'} />
            )}
            <Typography
              fontSize={'inherit'}
              fontFamily={'inherit'}
              fontWeight={'inherit'}
            >
              Energy Demand
            </Typography>
          </Stack>
          <Typography
            fontSize={'inherit'}
            fontFamily={'inherit'}
            fontWeight={'bold'}
          >
            {isLoading ? (
              <Skeleton variant='text' width={60} />
            ) : (
              `${safeFormatDecimal(energyDemand, 'SystemCardPopover.energyDemand')} MWh/Year`
            )}
          </Typography>
        </Stack>
        {/**third row - Capital Cost*/}
        <Stack
          direction={'row'}
          alignItems={'center'}
          justifyContent={'space-between'}
          spacing={1}
        >
          <Stack direction={'row'} alignItems={'center'} spacing={1}>
            <MonetizationOn fontSize={'small'} color={'primary'} />
            <Typography
              fontSize={'inherit'}
              fontFamily={'inherit'}
              fontWeight={'inherit'}
            >
              Capital Cost
            </Typography>
          </Stack>
          <Typography
            fontSize={'inherit'}
            fontFamily={'inherit'}
            fontWeight={'bold'}
          >
            {isLoading ? (
              <Skeleton variant='text' width={60} />
            ) : capitalCostNote !== undefined ? (
              capitalCostNote
            ) : (
              `${safeFormatUSD(capitalCost, 'SystemCardPopover.capitalCost')}/unit`
            )}
          </Typography>
        </Stack>
        {/**fourth row - Annual Cost*/}{' '}
        <Stack
          direction={'row'}
          alignItems={'center'}
          justifyContent={'space-between'}
          spacing={1}
        >
          <Stack direction={'row'} alignItems={'center'} spacing={1}>
            <RequestQuote fontSize={'small'} color={'primary'} />
            <Typography
              fontSize={'inherit'}
              fontFamily={'inherit'}
              fontWeight={'inherit'}
            >
              Annual Cost
            </Typography>
          </Stack>
          <Typography
            fontSize={'inherit'}
            fontFamily={'inherit'}
            fontWeight={'bold'}
          >
            {isLoading ? (
              <Skeleton variant='text' width={60} />
            ) : (
              `${safeFormatUSD(annualCost, 'SystemCardPopover.annualCost')}/unit/Year`
            )}
          </Typography>
        </Stack>
        {/**fifth row - Design Life*/}
        <Stack
          direction={'row'}
          alignItems={'center'}
          justifyContent={'space-between'}
          spacing={1}
        >
          <Stack direction={'row'} alignItems={'center'} spacing={1}>
            <Schedule fontSize={'small'} color={'primary'} />
            <Typography
              fontSize={'inherit'}
              fontFamily={'inherit'}
              fontWeight={'inherit'}
            >
              Design Life
            </Typography>
          </Stack>
          <Typography
            fontSize={'inherit'}
            fontFamily={'inherit'}
            fontWeight={'bold'}
          >
            {isLoading ? (
              <Skeleton variant='text' width={60} />
            ) : designLife !== undefined && designLife !== null ? (
              `${safeFormatDecimal(designLife, 'SystemCardPopover.designLife')} years`
            ) : (
              '\u2014'
            )}
          </Typography>
        </Stack>
      </Stack>
      {/** DIVIDER */}
      <Stack direction={'row'} justifyContent={'center'} width={'100%'}>
        <Divider
          orientation={'horizontal'}
          sx={{ width: '35%', color: 'text.secondary' }}
          flexItem
        />
      </Stack>
      {/** FOOTER / LIMIT / LIMIT DISCLAIMER TEXT */}
      <Stack spacing={0.5}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography
            fontSize={'inherit'}
            fontFamily={'inherit'}
            fontWeight={500}
          >
            Limit
          </Typography>
          <Typography
            fontSize={'inherit'}
            fontFamily={'inherit'}
            fontWeight={'bold'}
          >
            {limit ?? '\u2014'}
          </Typography>
        </Box>
        <Typography
          variant={'subtitle2'}
          textAlign={'center'}
          textTransform={'uppercase'}
          lineHeight={1.3}
          fontSize={{ sm: 4, md: 6, lg: 8 }}
        >
          Limits on components may be due to maximum required to meet needs,
          insufficient space to support more, or additional parameters.
        </Typography>
      </Stack>{' '}
    </Stack>
  );
};
